import { CheckSquare, ChevronDown, MoreHorizontal, Search, SlidersHorizontal } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { operationConfig } from "@/data/afterflowData";
import SectionHeader from "@/components/SectionHeader";
import { api } from "@/lib/api";

export default function OperationsPage({ path }) {
  const config = operationConfig[path];
  const [activeTab, setActiveTab] = useState(config.tabs[0]);
  const [search, setSearch] = useState("");
  const [liveRows, setLiveRows] = useState([]);
  const kindByPath = { "/app/requests": "request", "/app/returns": "return", "/app/refunds": "refund", "/app/exchanges": "exchange", "/app/warranty": "warranty" };

  useEffect(() => {
    const status = activeTab === config.tabs[1] ? "review" : "";
    const params = new URLSearchParams({ query: search });
    if (kindByPath[path]) params.set("type", kindByPath[path]);
    if (status) params.set("status", status);
    api.get(`/operations?${params}`).then((response) => setLiveRows(response.data.items)).catch(() => setLiveRows([]));
  }, [activeTab, config.tabs, path, search]);
  const rows = useMemo(() => {
    const source = liveRows.map((row) => [row.customer_name || "—", row.type, row.order_number, `$${row.amount || 0}`, row.status, row.risk || "—", row.updated_at]);
    const matchedRows = source.filter((row) => {
      return row.join(" ").toLowerCase().includes(search.toLowerCase());
    });
    const activeIndex = config.tabs.indexOf(activeTab);

    if (activeIndex === 1) {
      return matchedRows.slice(0, 2);
    }
    if (activeIndex === 2) {
      return matchedRows.slice(2);
    }
    if (activeIndex === 3) {
      return [];
    }
    return matchedRows;
  }, [activeTab, config.tabs, liveRows, search]);

  return (
    <div className="page operations-page" data-testid={`${config.title.toLowerCase().replaceAll(" ", "-")}-page`}>
      <SectionHeader {...config} />
      <section className="queue-summary" data-testid="queue-summary">
        <div><span>Open now</span><strong>24</strong><small>8 require attention</small></div>
        <div><span>Handled today</span><strong>91</strong><small className="positive-change">↗ 18% vs yesterday</small></div>
        <div><span>Avg. resolution</span><strong>3.2h</strong><small>Within your SLA target</small></div>
      </section>
      <section className="data-shell" data-testid="operations-data-table">
        <div className="table-tabs" role="tablist">
          {config.tabs.map((tab) => <button key={tab} onClick={() => setActiveTab(tab)} className={tab === activeTab ? "tab-active" : ""} data-testid={`tab-${tab.toLowerCase().replaceAll(" ", "-")}`}>{tab}</button>)}
        </div>
        <div className="table-toolbar">
          <label className="table-search" data-testid="operations-search-field"><Search size={16} /><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search customers, orders, requests" data-testid="operations-search-input" /></label>
          <button className="secondary-button" data-testid="advanced-filters-button"><SlidersHorizontal size={15} />Filters <span className="filter-count">2</span></button>
          <button className="secondary-button" data-testid="bulk-actions-button"><CheckSquare size={15} />Bulk actions <ChevronDown size={14} /></button>
        </div>
        <div className="table-scroll"><table><thead><tr><th><input type="checkbox" data-testid="select-all-rows-checkbox" /></th>{config.columns.map((column) => <th key={column} data-testid={`column-${column.toLowerCase().replaceAll(" ", "-")}`}>{column}</th>)}<th /></tr></thead><tbody>{rows.map((row, index) => <tr key={`${row[0]}-${index}`} data-testid={`operations-row-${index + 1}`}><td><input type="checkbox" data-testid={`select-row-${index + 1}-checkbox`} /></td>{row.map((cell, cellIndex) => <td key={`${cell}-${cellIndex}`}>{cellIndex === 4 ? <span className={`status-pill status-${cell.toLowerCase().replaceAll(" ", "-")}`}>{cell}</span> : cellIndex === 5 ? <span className={`risk-value risk-${cell.toLowerCase()}`}>{cell}</span> : cell}</td>)}<td><button className="row-menu" onClick={() => liveRows[index] && api.post(`/operations/${liveRows[index].operation_id}/decision`, { action: "approve" }).then(() => setActiveTab(config.tabs[0]))} data-testid={`row-${index + 1}-menu-button`} aria-label="Approve row"><MoreHorizontal size={17} /></button></td></tr>)}</tbody></table></div>
        {!rows.length && <div className="empty-state" data-testid="empty-table-state"><Search size={24} /><h2>No matching operations</h2><p>Try a different customer name, order number, or filter.</p></div>}
      </section>
    </div>
  );
}